/** MQ ADC defaults + ENS160 eCO2/TVOC defaults. */
export const DEFAULT_SENSOR_THRESHOLDS = {
    mq2_warning: 2800,
    mq2_alarm: 3600,
    mq135_warning: 1800,
    mq135_alarm: 2600,
    eco2_warning: 1000,
    eco2_alarm: 1500,
    tvoc_warning: 250,
    tvoc_alarm: 500,
};

/** @returns {"mq2"|"mq135"|"ens160"|"water"|"both"} */
export function resolveSensorChannel(sensor) {
    const config = sensor?.config && typeof sensor.config === "object" ? sensor.config : {};
    const channel = String(config.channel || "").trim().toLowerCase();
    if (channel === "ens160" || channel === "eco") return "ens160";
    if (channel === "mq135" || channel === "air") return "mq135";
    if (channel === "mq2" || channel === "smoke" || channel === "gas") return "mq2";
    if (channel === "water") return "water";

    const type = String(sensor?.sensor_type || "").toLowerCase();
    const uid = String(sensor?.device_uid || "").toLowerCase();
    if (/ens160|eco|aht21|tvoc|якість/.test(type) || uid.includes("ens160")) return "ens160";
    if (/вода|water|протік|протеч|leak/.test(type) || uid.includes("water")) return "water";
    if (/повітря|воздух|air|mq.?135/.test(type) || uid.includes("mq135")) return "mq135";
    if (/дим|газ|smoke|gas|mq.?2/.test(type) || uid.includes("mq2")) return "mq2";
    return "both";
}

export function normalizeSensorThresholds(raw) {
    const source = raw && typeof raw === "object" ? raw : {};
    const toInt = (value, fallback, max = 4095) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return fallback;
        return Math.max(0, Math.min(max, Math.round(n)));
    };

    let mq2Warning = toInt(source.mq2_warning ?? source.mq2Warning, DEFAULT_SENSOR_THRESHOLDS.mq2_warning);
    let mq2Alarm = toInt(source.mq2_alarm ?? source.mq2Alarm, DEFAULT_SENSOR_THRESHOLDS.mq2_alarm);
    let mq135Warning = toInt(source.mq135_warning ?? source.mq135Warning, DEFAULT_SENSOR_THRESHOLDS.mq135_warning);
    let mq135Alarm = toInt(source.mq135_alarm ?? source.mq135Alarm, DEFAULT_SENSOR_THRESHOLDS.mq135_alarm);
    let eco2Warning = toInt(source.eco2_warning ?? source.eco2Warning, DEFAULT_SENSOR_THRESHOLDS.eco2_warning, 60000);
    let eco2Alarm = toInt(source.eco2_alarm ?? source.eco2Alarm, DEFAULT_SENSOR_THRESHOLDS.eco2_alarm, 60000);
    let tvocWarning = toInt(source.tvoc_warning ?? source.tvocWarning, DEFAULT_SENSOR_THRESHOLDS.tvoc_warning, 60000);
    let tvocAlarm = toInt(source.tvoc_alarm ?? source.tvocAlarm, DEFAULT_SENSOR_THRESHOLDS.tvoc_alarm, 60000);

    if (mq2Alarm < mq2Warning) mq2Alarm = mq2Warning;
    if (mq135Alarm < mq135Warning) mq135Alarm = mq135Warning;
    if (eco2Alarm < eco2Warning) eco2Alarm = eco2Warning;
    if (tvocAlarm < tvocWarning) tvocAlarm = tvocWarning;

    return {
        mq2_warning: mq2Warning,
        mq2_alarm: mq2Alarm,
        mq135_warning: mq135Warning,
        mq135_alarm: mq135Alarm,
        eco2_warning: eco2Warning,
        eco2_alarm: eco2Alarm,
        tvoc_warning: tvocWarning,
        tvoc_alarm: tvocAlarm,
    };
}

export function thresholdsFromSensorConfig(config) {
    const thresholds = config && typeof config === "object" ? config.thresholds : null;
    return normalizeSensorThresholds(thresholds);
}

export function mergeThresholdsIntoConfig(config, thresholds, channel = "both") {
    const base = config && typeof config === "object" && !Array.isArray(config) ? { ...config } : {};
    const normalized = normalizeSensorThresholds(thresholds);
    const prev = normalizeSensorThresholds(base.thresholds);
    if (channel === "mq2") {
        base.thresholds = {
            ...prev,
            mq2_warning: normalized.mq2_warning,
            mq2_alarm: normalized.mq2_alarm,
        };
        base.channel = "mq2";
    } else if (channel === "mq135") {
        base.thresholds = {
            ...prev,
            mq135_warning: normalized.mq135_warning,
            mq135_alarm: normalized.mq135_alarm,
        };
        base.channel = "mq135";
    } else if (channel === "ens160") {
        base.thresholds = {
            ...prev,
            eco2_warning: normalized.eco2_warning,
            eco2_alarm: normalized.eco2_alarm,
            tvoc_warning: normalized.tvoc_warning,
            tvoc_alarm: normalized.tvoc_alarm,
            // Alias for quiet-room / legacy ingest using reading_air as eCO2.
            mq135_warning: normalized.eco2_warning,
            mq135_alarm: normalized.eco2_alarm,
        };
        base.channel = "ens160";
    } else if (channel === "water") {
        base.thresholds = {
            ...prev,
            water_alarm: 1,
        };
        base.channel = "water";
    } else {
        base.thresholds = normalized;
    }
    return base;
}

export function mergeTimezoneIntoConfig(config, timezone) {
    const base = config && typeof config === "object" && !Array.isArray(config) ? { ...config } : {};
    const tz = String(timezone || "").trim();
    if (tz) {
        base.timezone = tz;
    }
    return base;
}

export const DEFAULT_SENSOR_DEVICE_SETTINGS = {
    heartbeat_interval_sec: 900,
    buzzer_enabled: true,
};

export const DEFAULT_WATER_CHECK_INTERVAL_SEC = 1800;
export const MIN_WATER_CHECK_INTERVAL_SEC = 300;

export function normalizeSensorDeviceSettings(raw, config = null, { channel = null } = {}) {
    const source = raw && typeof raw === "object" ? raw : {};
    const fromConfig = config && typeof config === "object" ? config : {};
    const isWater = channel === "water";
    const minSec = isWater ? MIN_WATER_CHECK_INTERVAL_SEC : 15;
    const defaultSec = isWater ? DEFAULT_WATER_CHECK_INTERVAL_SEC : DEFAULT_SENSOR_DEVICE_SETTINGS.heartbeat_interval_sec;
    const toInt = (value, fallback) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return fallback;
        return Math.max(minSec, Math.min(86400, Math.round(n)));
    };
    const toBool = (value, fallback) => {
        if (typeof value === "boolean") return value;
        if (value === 0 || value === "0" || value === "false" || value === "off") return false;
        if (value === 1 || value === "1" || value === "true" || value === "on") return true;
        return fallback;
    };
    const interval = toInt(
        source.heartbeat_interval_sec
            ?? source.heartbeatIntervalSec
            ?? source.check_interval_sec
            ?? fromConfig.heartbeat_interval_sec
            ?? fromConfig.check_interval_sec,
        defaultSec
    );
    return {
        heartbeat_interval_sec: interval,
        check_interval_sec: interval,
        buzzer_enabled: toBool(
            source.buzzer_enabled ?? source.buzzerEnabled ?? fromConfig.buzzer_enabled,
            DEFAULT_SENSOR_DEVICE_SETTINGS.buzzer_enabled
        ),
    };
}

export function deviceSettingsFromSensorConfig(config, sensor = null) {
    const channel = sensor ? resolveSensorChannel(sensor) : (config?.channel === "water" ? "water" : null);
    return normalizeSensorDeviceSettings(null, config, { channel });
}

export function mergeDeviceSettingsIntoConfig(config, settings, sensor = null) {
    const base = config && typeof config === "object" && !Array.isArray(config) ? { ...config } : {};
    const channel = sensor ? resolveSensorChannel({ ...sensor, config: base }) : (base.channel === "water" ? "water" : null);
    const normalized = normalizeSensorDeviceSettings(settings, base, { channel });
    base.heartbeat_interval_sec = normalized.heartbeat_interval_sec;
    base.check_interval_sec = normalized.check_interval_sec;
    base.buzzer_enabled = normalized.buzzer_enabled;
    if (channel === "water") {
        base.channel = "water";
    }
    return base;
}
